import type { ReactNode } from 'react'
import { AlertTriangleIcon, InfoIcon, RefreshIcon } from './icons'
import Button from './Button'
import Spinner from './Spinner'

export interface StateProps {
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  action?: ReactNode
  className?: string
}

export function EmptyState({ title, description, icon, action, className = '' }: StateProps) {
  return (
    <div className={`state ${className}`.trim()}>
      <span className="state__icon">{icon ?? <InfoIcon size={24} />}</span>
      <p className="state__title">{title}</p>
      {description && <p className="state__text">{description}</p>}
      {action}
    </div>
  )
}

export interface ErrorStateProps extends Omit<StateProps, 'title'> {
  title?: ReactNode
  /** Rendered as a Retry button when supplied. */
  onRetry?: () => void
  /** Shown in small print — the correlation id operators need to find the request in the logs. */
  requestId?: string
}

export function ErrorState({
  title = 'Something went wrong',
  description,
  onRetry,
  requestId,
  className = '',
}: ErrorStateProps) {
  return (
    <div className={`state ${className}`.trim()} role="alert">
      <span className="state__icon state__icon--danger">
        <AlertTriangleIcon size={24} />
      </span>
      <p className="state__title">{title}</p>
      {description && <p className="state__text">{description}</p>}
      {onRetry && (
        <Button variant="outline" size="sm" iconStart={<RefreshIcon size={16} />} onClick={onRetry}>
          Try again
        </Button>
      )}
      {requestId && (
        <p className="state__text" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          Reference: <code>{requestId}</code>
        </p>
      )}
    </div>
  )
}

export function LoadingState({ label = 'Loading…', className = '' }: { label?: string; className?: string }) {
  return (
    <div className={`state ${className}`.trim()}>
      <Spinner size={26} label={label} />
      <p className="state__text">{label}</p>
    </div>
  )
}
